// Touch and mouse gestures, independent of three.js: one finger (or the mouse) taps, and either pans
// the camera or draws (belts, a machine ghost) when the view asks for it; two fingers always pan and
// pinch to zoom (PRD section 19.1), and the wheel zooms.

export interface ScreenPoint {
  readonly x: number;
  readonly y: number;
}

/** What one finger does in a gesture: move the camera, or draw on the scene. */
export type OneFinger = 'pan' | 'draw';

/** Movement a finger may make and still count as a tap, in CSS pixels. */
export const TAP_SLOP = 10;
/** Longest press that still counts as a tap, in milliseconds. */
export const TAP_MS = 600;

export interface GestureHandlers {
  /** A gesture started (first pointer down). */
  readonly onStart?: () => void;
  /** Decides, when the first finger lands, whether it pans (the default) or draws. */
  readonly oneFinger?: (p: ScreenPoint) => OneFinger;
  /** A short touch that barely moved, in pan mode. */
  readonly onTap?: (p: ScreenPoint) => void;
  /** Drawing with one finger: it landed, moved and lifted. */
  readonly onDrawStart?: (p: ScreenPoint) => void;
  readonly onDraw?: (p: ScreenPoint) => void;
  readonly onDrawEnd?: (p: ScreenPoint) => void;
  /** A second finger landed while drawing: the drawing is abandoned for a camera gesture. */
  readonly onDrawCancel?: () => void;
  /** Move the world so that the point under `from` ends under `to`. */
  readonly onPan: (from: ScreenPoint, to: ScreenPoint) => void;
  /** Zoom by `factor` (above 1 zooms in) keeping the point under `at` fixed. */
  readonly onZoom: (factor: number, at: ScreenPoint) => void;
  /** The last pointer was released. */
  readonly onEnd?: () => void;
}

export const centroid = (points: readonly ScreenPoint[]): ScreenPoint => {
  const n = points.length || 1;
  return {
    x: points.reduce((s, p) => s + p.x, 0) / n,
    y: points.reduce((s, p) => s + p.y, 0) / n,
  };
};

export const distance = (a: ScreenPoint, b: ScreenPoint): number => Math.hypot(a.x - b.x, a.y - b.y);

/** Zoom factor for a wheel or trackpad-pinch delta; pinches on a trackpad arrive as ctrl+wheel. */
export function wheelZoomFactor(deltaY: number, deltaMode: number, ctrlKey: boolean): number {
  const pixels = deltaMode === 1 ? deltaY * 16 : deltaMode === 2 ? deltaY * 400 : deltaY;
  return Math.exp(-pixels * (ctrlKey ? 0.01 : 0.0015));
}

/** Tracks active pointers and turns their movement into taps, drawing, pan and zoom steps. */
export class GestureTracker {
  private readonly pointers = new Map<number, ScreenPoint>();
  private readonly handlers: GestureHandlers;
  private mode: OneFinger = 'pan';
  /** Where and when the gesture started, and whether it is still a tap candidate. */
  private start: { readonly p: ScreenPoint; readonly t: number } | undefined;
  private still = true;
  private drawing = false;

  constructor(handlers: GestureHandlers) {
    this.handlers = handlers;
  }

  down(id: number, p: ScreenPoint, now = 0): void {
    if (this.pointers.size === 0) {
      this.handlers.onStart?.();
      this.mode = this.handlers.oneFinger?.(p) ?? 'pan';
      this.start = { p, t: now };
      this.still = true;
      this.pointers.set(id, p);
      if (this.mode === 'draw') {
        this.drawing = true;
        this.handlers.onDrawStart?.(p);
      }
      return;
    }
    // A second finger: no tap, no drawing; from now on this gesture moves the camera.
    this.still = false;
    this.mode = 'pan';
    if (this.drawing) {
      this.drawing = false;
      this.handlers.onDrawCancel?.();
    }
    this.pointers.set(id, p);
  }

  move(id: number, p: ScreenPoint): void {
    const prev = this.pointers.get(id);
    if (!prev) return;
    const before = [...this.pointers.values()];
    this.pointers.set(id, p);
    const after = [...this.pointers.values()];
    if (after.length === 1) {
      if (this.drawing) {
        this.handlers.onDraw?.(p);
        return;
      }
      if (this.still) {
        // A finger that has not left the tap slop does not move the camera yet; once it does, the
        // pan catches up from where it landed, so no movement is lost.
        const origin = this.start?.p ?? prev;
        if (distance(origin, p) <= TAP_SLOP) return;
        this.still = false;
        this.handlers.onPan(origin, p);
        return;
      }
      this.handlers.onPan(prev, p);
      return;
    }
    // Two or more fingers: use the first two for the pinch and their centroid for the pan.
    const [a0, b0] = before;
    const [a1, b1] = after;
    if (!a0 || !b0 || !a1 || !b1) return;
    const c0 = centroid([a0, b0]);
    const c1 = centroid([a1, b1]);
    this.handlers.onPan(c0, c1);
    const d0 = distance(a0, b0);
    const d1 = distance(a1, b1);
    if (d0 > 1 && d1 > 1) this.handlers.onZoom(d1 / d0, c1);
  }

  up(id: number, now = 0): void {
    const p = this.pointers.get(id);
    if (!p || !this.pointers.delete(id)) return;
    if (this.pointers.size > 0) return;
    if (this.drawing) {
      this.drawing = false;
      this.handlers.onDrawEnd?.(p);
    } else if (this.still && this.start && now - this.start.t <= TAP_MS) {
      this.handlers.onTap?.(this.start.p);
    }
    this.start = undefined;
    this.handlers.onEnd?.();
  }

  cancelAll(): void {
    const had = this.pointers.size > 0;
    this.pointers.clear();
    if (this.drawing) {
      this.drawing = false;
      this.handlers.onDrawCancel?.();
    }
    this.start = undefined;
    if (had) this.handlers.onEnd?.();
  }
}

/** Connects a GestureTracker to an element's pointer and wheel events. Returns a cleanup function. */
export function bindGestures(element: HTMLElement, handlers: GestureHandlers): () => void {
  const tracker = new GestureTracker(handlers);
  const point = (e: PointerEvent | WheelEvent): ScreenPoint => {
    const rect = element.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };
  const onDown = (e: PointerEvent): void => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    try {
      element.setPointerCapture(e.pointerId);
    } catch {
      // Synthetic or already released pointers cannot be captured; tracking still works.
    }
    tracker.down(e.pointerId, point(e), e.timeStamp);
    e.preventDefault();
  };
  const onMove = (e: PointerEvent): void => {
    tracker.move(e.pointerId, point(e));
  };
  const onUp = (e: PointerEvent): void => {
    if (element.hasPointerCapture(e.pointerId)) element.releasePointerCapture(e.pointerId);
    tracker.up(e.pointerId, e.timeStamp);
  };
  const onCancel = (e: PointerEvent): void => {
    if (element.hasPointerCapture(e.pointerId)) element.releasePointerCapture(e.pointerId);
    // The system took the touch (a palm, a system gesture): never treat it as a tap or a finished drawing.
    tracker.cancelAll();
  };
  const onWheel = (e: WheelEvent): void => {
    e.preventDefault();
    handlers.onZoom(wheelZoomFactor(e.deltaY, e.deltaMode, e.ctrlKey), point(e));
  };
  const preventGesture = (e: Event): void => {
    e.preventDefault();
  };
  const onBlur = (): void => {
    tracker.cancelAll();
  };
  element.addEventListener('pointerdown', onDown);
  element.addEventListener('pointermove', onMove);
  element.addEventListener('pointerup', onUp);
  element.addEventListener('pointercancel', onCancel);
  element.addEventListener('wheel', onWheel, { passive: false });
  // Safari's own pinch-to-zoom of the page would fight the camera.
  element.addEventListener('gesturestart', preventGesture);
  window.addEventListener('blur', onBlur);
  return () => {
    element.removeEventListener('pointerdown', onDown);
    element.removeEventListener('pointermove', onMove);
    element.removeEventListener('pointerup', onUp);
    element.removeEventListener('pointercancel', onCancel);
    element.removeEventListener('wheel', onWheel);
    element.removeEventListener('gesturestart', preventGesture);
    window.removeEventListener('blur', onBlur);
  };
}
